import React, { createContext, useContext, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import T from "@/i18n/translations";
import { detectLangAndKey, buildEquivalentPath, DEFAULT_LANG, LANGS, ROUTE_KEYS, GUIDE_BASES, ROUTE_BASES, CAR_BASES } from "@/i18n/routeMap";
import { usePageMeta } from "@/contexts/PageMetaContext";

const LanguageContext = createContext({
  lang: DEFAULT_LANG,
  t: T[DEFAULT_LANG],
  setLang: () => {},
});

export const LanguageProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { slugMap } = usePageMeta() || {};
  const { lang, key } = useMemo(() => detectLangAndKey(location.pathname), [location.pathname]);

  const setLang = (target) => {
    if (!LANGS.includes(target) || target === lang) return;
    // If the active page registered a slug map and we're on a detail page with
    // per-language slugs (guides/routes), prefer that map. Vehicles share a
    // universal slug so the default behaviour is correct already.
    if (slugMap && typeof slugMap === "object") {
      const targetSlug = slugMap[target];
      if (targetSlug) {
        if (key === ROUTE_KEYS.GUIDE_DETAIL) return navigate(`${GUIDE_BASES[target]}/${targetSlug}`);
        if (key === ROUTE_KEYS.ROUTE_DETAIL) return navigate(`${ROUTE_BASES[target]}/${targetSlug}`);
        if (key === ROUTE_KEYS.CAR_DETAIL) return navigate(`${CAR_BASES[target]}/${targetSlug}`);
      }
      if (key === ROUTE_KEYS.GUIDE_DETAIL) return navigate(GUIDE_BASES[target]);
      if (key === ROUTE_KEYS.ROUTE_DETAIL) return navigate(ROUTE_BASES[target]);
    }
    const path = buildEquivalentPath(location.pathname, target);
    navigate(path);
  };

  const t = T[lang] || T[DEFAULT_LANG];
  const value = useMemo(() => ({ lang, t, setLang }), [lang, t, setLang]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLang = () => useContext(LanguageContext);

/** Read a multilingual {en,es,ru,fr,de,it,nl,pl} dict with fallback. */
export const pick = (dict, lang) => {
  if (!dict) return "";
  if (typeof dict === "string") return dict;
  return (
    dict[lang] || dict.en || dict.es || dict.ru ||
    dict.fr || dict.de || dict.it || dict.nl || dict.pl || ""
  );
};
